import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import PermissionManager, { RoutePermissions } from '../../managers/PermissionManager'
import UsersManager from '../../managers/UsersManager'
import { useAppSelector } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import ApprovalAudit from '../approval/ApprovalAudit'
import Approvals from '../approval/ApprovalsHub'
import ApprovalsUsers from '../approval/ApprovalsUsers'
import SchemaManager from '../editor/new_contract_schema/NewSchemaManager'
import RouterError from '../error_boundary/error_pages/RouterError'
import ErrorBoundary from '../error_boundary/ErrorBoundary'
import Flow from '../flows/Flow'
import FlowsPage from '../flows/FlowsPage'
import ResultPage from '../flows/multi_run/ResultPage'
import Administrator from '../routes/Administrator'
import ClaimsBreakdown from '../routes/ClaimsBreakdown'
import CompareContractsCsi from '../routes/CompareContractsCsi'
import ContractAnalysis from '../routes/ContractAnalysis'
import ContractDetails from '../routes/ContractDetails'
import ContractEditor from '../routes/ContractEditor'
import ContractManagerPage from '../routes/ContractManagerPage'
import ContractSchema from '../routes/ContractSchema'
import ContractsHub from '../routes/ContractsHub'
import CounterpartyDetails from '../routes/CounterpartyDetails'
import FundsBreakdown from '../routes/FundsBreakdown'
import GroupsEditor from '../routes/GroupsEditor'
import GroupsManagement from '../routes/GroupsManagement'
import Media from '../routes/Media'
import MemberDetails from '../routes/MemberDetails'
import Members from '../routes/Members'
import Network from '../routes/Network'
import Overview from '../routes/Overview'
import Playground from '../routes/Playground'
import ProviderContracts from '../routes/ProviderContracts'
import ProviderDetails from '../routes/ProviderDetails'
import ProviderPerformanceBreakdown from '../routes/ProviderPerformanceBreakdown'
import QualityMeasuresBreakdown from '../routes/QualityMeasuresBreakdown'
import { ReportEditor } from '../routes/ReportEditor'
import SettlementDetails from '../routes/SettlementDetails'
import TCOCSummary from '../routes/TCOCSummary'
import TooltipsAdmin from '../routes/TooltipsAdmin'
import UserProfile from '../routes/UserProfile'
import Workflows from '../routes/Workflows'
import RoutingManager from './Routing'
import Unauthorized from './Unauthorized'

export default function Router() {
    const dispatch = useDispatch()
    const isFromPopState = useRef(false)
    const route = useAppSelector(s => s.session.currentRoute)
    const params = useAppSelector(s => s.session.routeParams)
    const user = UsersManager.getCurrentUser()

    useEffect(() => {
        if (isFromPopState.current) {
            isFromPopState.current = false
            return
        }

        const path = RoutingManager.buildPath(route)

        const searchParams = new URLSearchParams()

        if (params && typeof params === 'object') {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    searchParams.set(key, String(value))
                }
            })
        }

        const query = searchParams.toString()
        const url = query ? `/${path}?${query}` : `/${path}`

        window.history.pushState(
            { route, params },
            '',
            url
        )
    }, [route, params])

    useEffect(() => {
        const onPop = (event: PopStateEvent) => {
            isFromPopState.current = true

            const strictRoute = RoutingManager.getRouteFromLocationStrict(window.location.pathname)

            if (!strictRoute) {
                dispatch(changeRoute({
                    route: 'overview'
                }))
                return
            }

            dispatch(changeRoute({
                route: event.state?.route ?? strictRoute,
                params: event.state?.params ?? null
            }))
        }

        window.addEventListener('popstate', onPop)

        return () => {
            window.removeEventListener('popstate', onPop)
        }
    }, [])

    const renderRouter = () => {
        switch (route) {
            case 'overview':
            default: return <Overview />
            case 'contracts': return <ContractsHub />
            case 'contract-details': return <ContractDetails />
            case 'provider-group': return <CounterpartyDetails />
            case 'provider': return <ProviderDetails />
            case 'quality-measures-breakdown': return <QualityMeasuresBreakdown />
            case 'claims-breakdown': return <ClaimsBreakdown />
            case 'funds-breakdown': return <FundsBreakdown />
            case 'provider-performance-breakdown': return <ProviderPerformanceBreakdown />
            case 'provider-contracts': return <ProviderContracts />
            case 'network': return <Network />
            case 'members': return <Members />
            case 'member-details': return <MemberDetails />
            case 'contracts-manager': return <ContractManagerPage />
            case 'contract-editor': return <ContractEditor />
            case 'playground': return <Playground />
            case 'tcoc-summary': return <TCOCSummary />
            case 'flows': return <FlowsPage />
            case 'flow': return <Flow />
            case 'contracts-analysis': return <ContractAnalysis />
            case 'compare-contracts-csi': return <CompareContractsCsi />
            case 'settlement-details': return <SettlementDetails />
            case 'user-profile': return <UserProfile />
            case 'workflows': return <Workflows />
            case 'tooltip-admin': return <TooltipsAdmin />
            case 'contract-schema': return <ContractSchema />
            case 'administrator': return <Administrator />
            case 'report-editor': return <ReportEditor />
            case 'media': return <Media />
            case 'approvals': return <Approvals />
            case 'users-approvals': return <ApprovalsUsers />
            case 'groups-management': return <GroupsManagement />
            case 'groups-editor': return <GroupsEditor />
            case 'approval-audit': return <ApprovalAudit />
            case 'multirun-result': return <ResultPage />
            case 'contract-schema-editor': return <SchemaManager />
        }
    }

    const permission = RoutePermissions[route]
    const isAllowed = !permission || PermissionManager.hasPermission(permission, user)

    if (!isAllowed && route !== 'user-profile') {
        return <Unauthorized />
    }

    return (
        <ErrorBoundary key={route} renderError={e => <RouterError error={e} />}>
            {renderRouter()}
        </ErrorBoundary>
    )
}
